import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter, placeholder as placeholderExt, drawSelection } from '@codemirror/view'
import { EditorState, type Extension } from '@codemirror/state'
import { defaultKeymap, history, historyKeymap, indentWithTab, undo, redo } from '@codemirror/commands'
import { markdown } from '@codemirror/lang-markdown'
import { syntaxHighlighting, defaultHighlightStyle, bracketMatching, indentOnInput } from '@codemirror/language'
import { autocompletion, closeBrackets, closeBracketsKeymap, completionKeymap } from '@codemirror/autocomplete'
import { oneDark } from '@codemirror/theme-one-dark'
import type { EditorAdapter, EditorPosition, EditorSelection, EditorOptions } from '../editor-adapter'

export function createCodeMirrorAdapter(container: HTMLElement, options: EditorOptions): Promise<EditorAdapter> {
  let view: EditorView
  const changeListeners: Array<(value: string) => void> = []
  const selectionListeners: Array<(selection: EditorSelection) => void> = []

  const getPositionFromOffset = (offset: number): EditorPosition => {
    const lineObj = view.state.doc.lineAt(Math.max(0, Math.min(offset, view.state.doc.length)))
    return { line: lineObj.number, column: offset - lineObj.from, offset }
  }

  const getOffsetFromPosition = (pos: EditorPosition): number => {
    const lineNum = Math.max(1, Math.min(pos.line, view.state.doc.lines))
    const lineObj = view.state.doc.line(lineNum)
    return lineObj.from + pos.column
  }

  const createExtensions = (): Extension[] => {
    const extensions: Extension[] = [
      history(),
      EditorState.tabSize.of(options.tabSize ?? 4),
      EditorView.lineWrapping,
      lineNumbers(),
      highlightActiveLine(),
      highlightActiveLineGutter(),
      drawSelection(),
      indentOnInput(),
      syntaxHighlighting(defaultHighlightStyle),
      bracketMatching(),
      closeBrackets(),
      markdown(),
      autocompletion(),
      EditorView.theme({
        '&': { fontSize: `${options.fontSize ?? 14}px`, height: '100%', backgroundColor: options.theme === 'dark' ? '#1e1e1e' : '#ffffff' },
        '&.cm-focused': { outline: 'none' },
        '.cm-content': { minHeight: '100%', lineHeight: '1.6', fontFamily: "'JetBrains Mono', Consolas, Menlo, monospace" },
        '.cm-scroller': { overflow: 'auto' },
        '.cm-gutters': { backgroundColor: options.theme === 'dark' ? '#1e1e1e' : '#f5f5f5', color: '#8c8c8c', border: 'none' },
        '.cm-activeLine': { backgroundColor: options.theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(22,119,255,0.05)' },
        '.cm-cursor': { borderLeftColor: options.theme === 'dark' ? '#fff' : '#000', borderLeftWidth: '2px' },
        '&.cm-focused .cm-selectionBackground': {
          backgroundColor: options.theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(22,119,255,0.2)',
        },
      }),
    ]

    if (options.placeholder) extensions.push(placeholderExt(options.placeholder))
    if (options.readOnly) extensions.push(EditorState.readOnly.of(true))
    if (options.theme === 'dark') extensions.push(oneDark)
    if (!options.lineNumbers) extensions.push(lineNumbers())

    extensions.push(
      keymap.of([...defaultKeymap, ...historyKeymap, ...completionKeymap, ...closeBracketsKeymap, indentWithTab])
    )

    extensions.push(
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          const value = update.state.doc.toString()
          changeListeners.forEach((l) => l(value))
        }
        if (update.selectionSet) {
          const sel = update.state.selection.main
          const text = update.state.doc.sliceString(sel.from, sel.to)
          selectionListeners.forEach((l) => l({ start: getPositionFromOffset(sel.from), end: getPositionFromOffset(sel.to), isEmpty: sel.empty, text }))
        }
      })
    )

    return extensions
  }

  return new Promise((resolve) => {
    view = new EditorView({
      parent: container,
      state: EditorState.create({ doc: options.value ?? '', extensions: createExtensions() }),
    })

    const adapter: EditorAdapter = {
      getValue() { return view.state.doc.toString() },

      setValue(value: string) {
        view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: value } })
      },

      getSelection(): EditorSelection {
        const sel = view.state.selection.main
        return { start: getPositionFromOffset(sel.from), end: getPositionFromOffset(sel.to), isEmpty: sel.empty, text: view.state.doc.sliceString(sel.from, sel.to) }
      },

      setSelection(start: EditorPosition, end?: EditorPosition) {
        view.dispatch({ selection: { anchor: getOffsetFromPosition(start), head: getOffsetFromPosition(end ?? start) } })
        view.focus()
      },

      replaceSelection(text: string) {
        view.dispatch(view.state.replaceSelection(text))
      },

      insertText(text: string) {
        view.dispatch(view.state.replaceSelection(text))
      },

      wrapSelection(prefix: string, suffix: string) {
        const sel = view.state.selection.main
        const selected = view.state.doc.sliceString(sel.from, sel.to)
        if (sel.empty) {
          const insert = prefix + selected + suffix
          view.dispatch({ changes: { from: sel.from, to: sel.to, insert }, selection: { anchor: sel.from + prefix.length, head: sel.from + prefix.length } })
        } else {
          view.dispatch({ changes: { from: sel.from, to: sel.to, insert: prefix + selected + suffix }, selection: { anchor: sel.from + prefix.length, head: sel.to + prefix.length } })
        }
        view.focus()
      },

      wrapLine(prefix: string, suffix: string) {
        const sel = view.state.selection.main
        const startLine = view.state.doc.lineAt(sel.from)
        const endLine = view.state.doc.lineAt(sel.to)
        const changes: Array<{ from: number; to: number; insert: string }> = []
        for (let lineNum = startLine.number; lineNum <= endLine.number; lineNum++) {
          const lineObj = view.state.doc.line(lineNum)
          const idx = /^\s*/.exec(lineObj.text)?.[0].length ?? 0
          changes.push({ from: lineObj.from + idx, to: lineObj.to, insert: prefix + lineObj.text.slice(idx) + suffix })
        }
        view.dispatch({ changes })
        view.focus()
      },

      toggleLinePrefix(prefix: string) {
        const sel = view.state.selection.main
        const startLine = view.state.doc.lineAt(sel.from)
        const endLine = view.state.doc.lineAt(sel.to)
        const mode: boolean[] = []
        for (let lineNum = startLine.number; lineNum <= endLine.number; lineNum++) {
          const lineObj = view.state.doc.line(lineNum)
          const idx = /^[ \t]*/.exec(lineObj.text)?.[0].length ?? 0
          mode.push(lineObj.text.slice(idx).startsWith(prefix))
        }
        const alreadyApplied = mode.every(Boolean)
        const changes: Array<{ from: number; to: number; insert: string }> = []
        for (let lineNum = startLine.number; lineNum <= endLine.number; lineNum++) {
          const lineObj = view.state.doc.line(lineNum)
          const idx = /^[ \t]*/.exec(lineObj.text)?.[0].length ?? 0
          if (alreadyApplied) {
            changes.push({ from: lineObj.from, to: lineObj.from + idx + prefix.length, insert: lineObj.text.slice(0, idx) })
          } else {
            changes.push({ from: lineObj.from + idx, to: lineObj.from + idx, insert: prefix })
          }
        }
        view.dispatch({ changes })
        view.focus()
      },

      indentLines() {
        const sel = view.state.selection.main
        const startLine = view.state.doc.lineAt(sel.from)
        const endLine = view.state.doc.lineAt(sel.to)
        const changes: Array<{ from: number; to: number; insert: string }> = []
        for (let lineNum = startLine.number; lineNum <= endLine.number; lineNum++) {
          const lineObj = view.state.doc.line(lineNum)
          changes.push({ from: lineObj.from, to: lineObj.from, insert: '  ' })
        }
        view.dispatch({ changes })
        view.focus()
      },

      outdentLines() {
        const sel = view.state.selection.main
        const startLine = view.state.doc.lineAt(sel.from)
        const endLine = view.state.doc.lineAt(sel.to)
        const changes: Array<{ from: number; to: number; insert: string }> = []
        for (let lineNum = startLine.number; lineNum <= endLine.number; lineNum++) {
          const lineObj = view.state.doc.line(lineNum)
          const m = /^ {0,4}|\t/.exec(lineObj.text)
          if (m) changes.push({ from: lineObj.from, to: lineObj.from + m[0].length, insert: '' })
        }
        view.dispatch({ changes })
        view.focus()
      },

      insertLink() {
        const sel = view.state.selection.main
        const selected = view.state.doc.sliceString(sel.from, sel.to)
        const label = selected || '链接文字'
        view.dispatch({ changes: { from: sel.from, to: sel.to, insert: `[${label}](https://)` } })
        view.focus()
      },

      insertImage() {
        const sel = view.state.selection.main
        const selected = view.state.doc.sliceString(sel.from, sel.to)
        const label = selected || '图片描述'
        view.dispatch({ changes: { from: sel.from, to: sel.to, insert: `![${label}](https://)` } })
        view.focus()
      },

      insertTable() {
        const sel = view.state.selection.main
        const table = '| 列1 | 列2 | 列3 |\n| --- | --- | --- |\n| 单元格 | 单元格 | 单元格 |\n| 单元格 | 单元格 | 单元格 |'
        view.dispatch({ changes: { from: sel.from, to: sel.from, insert: table } })
        view.focus()
      },

      insertHorizontalRule() {
        const sel = view.state.selection.main
        view.dispatch({ changes: { from: sel.from, to: sel.from, insert: '\n---\n' } })
        view.focus()
      },

      insertFootnote() {
        const count = view.state.doc.toString().match(/\[\^(\d+)\]/g)?.length ?? 0
        const n = count + 1
        const sel = view.state.selection.main
        view.dispatch({ changes: { from: sel.to, insert: ` [^${n}]` } })
        view.focus()
      },

      undo() { undo(view) },
      redo() { redo(view) },

      canUndo(): boolean { return true },
      canRedo(): boolean { return true },

      focus() { view.focus() },

      onChange(callback: (value: string) => void) {
        changeListeners.push(callback)
        return () => {
          const i = changeListeners.indexOf(callback)
          if (i >= 0) changeListeners.splice(i, 1)
        }
      },

      onSelectionChange(callback: (selection: EditorSelection) => void) {
        selectionListeners.push(callback)
        return () => {
          const i = selectionListeners.indexOf(callback)
          if (i >= 0) selectionListeners.splice(i, 1)
        }
      },

      getLineCount() { return view.state.doc.lines },
      getLineContent(line: number) {
        try { return view.state.doc.line(line).text } catch { return '' }
      },
      setLineContent(line: number, content: string) {
        try {
          const lineObj = view.state.doc.line(line)
          view.dispatch({ changes: { from: lineObj.from, to: lineObj.to, insert: content } })
        } catch { /* out of range */ }
      },

      isSelectionWrapped(prefix: string, suffix: string) {
        const sel = view.state.selection.main
        const text = view.state.doc.sliceString(sel.from, sel.to)
        return text.startsWith(prefix) && text.endsWith(suffix)
      },

      dispose() {
        changeListeners.length = 0
        selectionListeners.length = 0
        view.destroy()
      },
    }

    resolve(adapter)
  })
}